import { useCallback, useEffect, useMemo, useState } from "react";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import {
  applyDevRoleGroups,
  applyDevRoleImpersonation,
  clearDevRoleSwitch,
  fetchDevRoleCatalog,
  fetchDevRoleSwitchState,
  fetchDevRoleUserGroups,
  employeeListHidesNames,
  employeeOptionText,
  searchDevRoleEmployees,
  type DevRoleCatalog,
  type DevRoleEmployeeSearchHit,
  type DevRoleSwitchState,
} from "@/lib/devRoleSwitch";

function sortedUnique(groups: string[]): string[] {
  return [...new Set(groups.map((group) => group.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "de"),
  );
}

/** Nur simulierte Rollen senden – Dev-Zugang und echte Admin-Rollen bleiben serverseitig. */
function groupsForApply(
  selected: Iterable<string>,
  preserve: string[],
  realGroups: string[] | undefined,
): string[] {
  const preserveKeys = new Set(preserve.map((group) => group.toLowerCase()));
  const realKeys = new Set(
    (realGroups || []).map((group) => group.trim().toLowerCase()).filter(Boolean),
  );
  return [...selected].filter((group) => {
    const key = group.trim().toLowerCase();
    return key && !preserveKeys.has(key) && !realKeys.has(key);
  });
}

type EmployeePick = DevRoleEmployeeSearchHit;

interface DevRoleSwitcherPanelProps {
  onApplied?: () => void;
}

export function DevRoleSwitcherPanel({ onApplied }: DevRoleSwitcherPanelProps) {
  const [state, setState] = useState<DevRoleSwitchState | null>(null);
  const [catalog, setCatalog] = useState<DevRoleCatalog | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeePick, setEmployeePick] = useState<AutocompleteOption<EmployeePick> | null>(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [namesMissing, setNamesMissing] = useState(false);

  const load = useCallback(async () => {
    const result = await fetchDevRoleSwitchState();
    if (!result.ok) {
      setState(null);
      setCatalog(null);
      setApiError(result.message);
      return;
    }
    setState(result.state);
    setApiError(null);
    const preserveKeys = new Set(
      (result.state.dev_preserve_groups || ["App-Admins", "App-Admin", "Admins"]).map((group) =>
        group.toLowerCase(),
      ),
    );
    const realKeys = new Set((result.state.groups_real || []).map((group) => group.toLowerCase()));
    setSelected(
      new Set(
        (result.state.active ? result.state.groups : []).filter((group) => {
          const key = group.toLowerCase();
          return key && !preserveKeys.has(key) && !realKeys.has(key);
        }),
      ),
    );
    if (result.state.impersonate_login) {
      setSelectedEmployee(result.state.impersonate_login);
    }
    try {
      const nextCatalog = await fetchDevRoleCatalog();
      setCatalog(nextCatalog);
    } catch {
      setCatalog({ employees: [], groups: [], employee_count: 0, dev_preserve_groups: [] });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const preserveList = catalog?.dev_preserve_groups?.length
    ? catalog.dev_preserve_groups
    : state?.dev_preserve_groups || ["App-Admins", "App-Admin", "Admins"];
  const preserveLabel = preserveList.join(", ");

  const available = useMemo(() => {
    if (!state) return [];
    return sortedUnique([
      ...(catalog?.groups || []),
      ...state.available_groups,
      ...state.groups,
      ...selected,
    ]);
  }, [state, catalog, selected]);

  const loadEmployeeOptions = useCallback(
    async (query: string): Promise<AutocompleteOption<EmployeePick>[]> => {
      const rows = await searchDevRoleEmployees(query, 500);
      const options = rows.map((row) => {
        const shown = employeeOptionText(row);
        return {
          key: row.login,
          label: shown.label,
          description: shown.description,
          value: row,
        };
      });
      setNamesMissing(employeeListHidesNames(options.map((option) => option.label)));
      return options;
    },
    [],
  );

  async function onEmployeeChange(next: AutocompleteOption<EmployeePick> | null) {
    setEmployeePick(next);
    if (!next) {
      setSelectedEmployee("");
      return;
    }
    const login = next.value.login;
    setSelectedEmployee(login);
    setGroupsLoading(true);
    try {
      const groups = await fetchDevRoleUserGroups(login, next.value.email || next.value.subtitle);
      setSelected(new Set(groups));
      setApiError(null);
    } catch {
      setSelected(new Set(next.value.groups || []));
      setApiError(
        "Gruppen für diesen Benutzer konnten nicht geladen werden – Gruppen manuell wählen oder „Rolle übernehmen“.",
      );
    } finally {
      setGroupsLoading(false);
    }
  }

  function toggle(group: string, checked: boolean) {
    setSelectedEmployee("");
    setEmployeePick(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(group);
      else next.delete(group);
      return next;
    });
  }

  function applyPreset(groups: string[]) {
    setSelectedEmployee("");
    setEmployeePick(null);
    setSelected(new Set(groups));
  }

  async function finish(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      onApplied?.();
      window.location.reload();
    } catch (err) {
      setBusy(false);
      setApiError(err instanceof Error ? err.message : "Fehler");
    }
  }

  if (apiError && !state) {
    return <p className="text-sm text-amber-700 dark:text-amber-300">{apiError}</p>;
  }

  if (!state || !catalog) {
    return <p className="text-sm text-label-tertiary">Lade Katalog …</p>;
  }

  const picked = groupsForApply(selected, preserveList, state.groups_real);
  const employeeSummary = employeePick
    ? [employeePick.label, employeePick.description].filter(Boolean).join(" · ")
    : selectedEmployee;

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-label-secondary">
        In der Simulation siehst du Campdesk wie mit den gewählten Gruppen. Nur für den
        Dev-Zugang bleiben intern aktiv: {preserveLabel}. Deine übrigen Rollen, zum Beispiel
        Camping-Admin, werden nicht mitgeschleppt.
      </p>

      <div className="space-y-2">
        <LockedAutocomplete<EmployeePick>
          label="Mitarbeiter"
          placeholder="Name oder E-Mail suchen …"
          hint="Mitglieder der Gruppe App-Nutzer."
          emptyText="Kein Mitglied der Gruppe App-Nutzer gefunden."
          minChars={0}
          debounceMs={200}
          value={employeePick}
          disabled={busy || groupsLoading}
          loadOptions={loadEmployeeOptions}
          onChange={(next) => void onEmployeeChange(next)}
        />
        {namesMissing ? (
          <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-300">
            Für die anderen Konten liefert Entra nur die Objekt-ID. Einmal abmelden und neu
            anmelden, damit Campdesk Name und E-Mail der Mitarbeiter lesen darf.
          </p>
        ) : null}
        <button
          type="button"
          disabled={busy || !selectedEmployee || groupsLoading}
          className="w-full rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          onClick={() =>
            void finish(() =>
              applyDevRoleImpersonation(
                selectedEmployee,
                employeePick?.value.email || employeePick?.value.subtitle,
              ),
            )
          }
        >
          Rolle dieses Mitarbeiters übernehmen
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {state.presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            disabled={busy}
            className="rounded-full border border-amber-500/45 bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-900 hover:bg-amber-500/25 dark:text-amber-200"
            title={preset.groups.join(", ")}
            onClick={() => applyPreset(preset.groups)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div
        className="max-h-[min(16rem,42vh)] overflow-y-auto rounded-lg border border-amber-500/40 bg-card shadow-inner"
        role="group"
        aria-label="Gruppen auswählen"
      >
        {available.map((group) => (
          <label
            key={group}
            className="flex cursor-pointer items-center gap-2 border-b border-separator/50 px-3 py-2.5 text-sm text-label last:border-b-0 hover:bg-amber-500/10"
          >
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-amber-600"
              checked={selected.has(group)}
              disabled={busy || groupsLoading}
              onChange={(event) => toggle(group, event.target.checked)}
            />
            <span>{group}</span>
          </label>
        ))}
      </div>

      <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
        {groupsLoading
          ? "Gruppen werden geladen …"
          : picked.length
            ? `Auswahl (${picked.length}): ${picked.join(", ")}`
            : selected.size
              ? "Die markierten Gruppen sind schon deine echten Rollen und ändern die Sicht nicht."
              : "Gruppen wählen oder Mitarbeiter-Rolle übernehmen."}
        {employeeSummary ? ` · Mitarbeiter: ${employeeSummary}` : ""}
      </p>

      <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-3">
        <button
          type="button"
          disabled={busy}
          className="rounded-lg border border-separator bg-fill px-3 py-1.5 text-xs font-semibold text-label hover:bg-fill-tertiary"
          onClick={() => {
            setSelected(new Set());
            setSelectedEmployee("");
            setEmployeePick(null);
          }}
        >
          Auswahl leeren
        </button>
        <button
          type="button"
          disabled={busy}
          className="rounded-lg border border-separator bg-fill px-3 py-1.5 text-xs font-semibold text-label hover:bg-fill-tertiary"
          onClick={() => void finish(clearDevRoleSwitch)}
        >
          Echte Gruppen
        </button>
        <button
          type="button"
          disabled={busy || picked.length === 0}
          className="rounded-lg border border-amber-600/50 bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-label hover:bg-amber-500/25 disabled:opacity-50"
          onClick={() => void finish(() => applyDevRoleGroups(picked))}
        >
          Gruppen übernehmen
        </button>
      </div>

      {apiError ? <p className="text-xs text-red-600">{apiError}</p> : null}
    </div>
  );
}
